import PropTypes from 'prop-types'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import { Fragment, useMemo } from 'react'
import { TEMPLATE_VARIABLES } from '../consts'

/**
 * Converts a CSS style string into a React-compatible style object.
 * @param {string} cssString - CSS string (e.g. "color: red; font-size: 12px")
 * @returns {Object} React style object
 */
const parseCssString = (cssString) => {
  const style = {}
  cssString.split(';').forEach((declaration) => {
    const [property, ...valueParts] = declaration.split(':')
    if (property && valueParts.length) {
      const camelCase = property
        .trim()
        .replace(/-([a-z])/g, (_, c) => c.toUpperCase())
      style[camelCase] = valueParts.join(':').trim()
    }
  })
  return style
}

/**
 * Parses an HTML template string, replacing {{variable}} placeholders with
 * highlighted, interactive React elements that show the variable name on hover.
 * Only whitelisted HTML tags are rendered to prevent XSS.
 * @param {string} html - HTML string potentially containing {{variable}} placeholders
 * @param {string} highlightColor - CSS color for the variable underline decoration
 * @returns {React.ReactNode[]|null} Array of React elements, or null if html is falsy
 */
const parseTemplateWithVariables = (html, highlightColor) => {
  if (!html) return null

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const allowedTags = new Set([
    'p',
    'strong',
    'em',
    'ul',
    'ol',
    'li',
    'span',
    'div',
    'br',
    'hr',
    'b',
    'i',
    'u',
    'a',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ])

  const voidElements = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ])

  const processNode = (node, key = 0) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent
      const parts = []
      let lastIndex = 0
      const regex = /\{\{(\w+)\}\}/g
      let match

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index))
        }

        const varName = match[1]
        const varValue = TEMPLATE_VARIABLES[varName] || match[0]

        parts.push(
          <Tooltip
            key={`${key}-${match.index}`}
            title={`{{${varName}}}`}
            arrow
            enterDelay={200}
          >
            <span
              style={{
                textDecoration: 'underline',
                textDecorationColor: highlightColor,
                textDecorationThickness: '2px',
                cursor: 'help',
              }}
            >
              {varValue}
            </span>
          </Tooltip>,
        )

        lastIndex = regex.lastIndex
      }

      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex))
      }

      return parts.length > 0 ? parts : text
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const Tag = node.tagName.toLowerCase()

      if (!allowedTags.has(Tag)) return null

      const props = { key }

      if (Tag === 'a') {
        if (node.getAttribute('href')) {
          props.href = node
            .getAttribute('href')
            .replace(
              /\{\{(\w+)\}\}/g,
              (_, v) => TEMPLATE_VARIABLES[v] || `{{${v}}}`,
            )
        }
        if (node.getAttribute('target')) {
          props.target = node.getAttribute('target')
        }
        if (node.getAttribute('rel')) {
          props.rel = node.getAttribute('rel')
        }
      }

      if (node.getAttribute('style')) {
        props.style = parseCssString(node.getAttribute('style'))
      }

      if (Tag === 'hr') {
        props.style = {
          border: 'none',
          borderTop: '2px solid #ccc',
          width: '80%',
          margin: '16px auto',
          ...props.style,
        }
      }

      if (voidElements.has(Tag)) {
        return <Tag {...props} />
      }

      const children = Array.from(node.childNodes).map((child, i) =>
        processNode(child, `${key}-${i}`),
      )

      return <Tag {...props}>{children}</Tag>
    }

    return null
  }

  const { body } = doc
  return Array.from(body.childNodes).map((node, i) => (
    <Fragment key={i}>{processNode(node, i)}</Fragment>
  ))
}

const TemplatePreviewBox = ({ subject, body, channel }) => {
  const theme = useTheme()
  const primaryColor = theme.palette.primary.main

  const subjectContent = useMemo(
    () => (subject ? parseTemplateWithVariables(subject, primaryColor) : null),
    [subject, primaryColor],
  )
  const bodyContent = useMemo(
    () => (body ? parseTemplateWithVariables(body, primaryColor) : null),
    [body, primaryColor],
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {channel === 'email' && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: 'block' }}
          >
            Subject:
          </Typography>
          <Box
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              minHeight: 40,
              bgcolor: 'grey.50',
            }}
          >
            {subjectContent || (
              <Typography component="span" color="text.disabled">
                No subject
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: 'block' }}
        >
          Body:
        </Typography>
        <Box
          sx={{
            p: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            minHeight: 200,
            bgcolor: 'grey.50',
            overflow: 'auto',
            fontFamily: 'Helvetica, Arial, sans-serif',
            '& p': { mb: 1 },
          }}
        >
          {bodyContent || (
            <Typography component="span" color="text.disabled">
              No content
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

TemplatePreviewBox.propTypes = {
  subject: PropTypes.string,
  body: PropTypes.string,
  channel: PropTypes.string,
}

export default TemplatePreviewBox
